import { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Check } from 'lucide-react';
import { supabase, LogEntry } from '../../lib/supabase';
import { generateUUID } from '../../lib/uuid';

interface MissingMileageModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSaved: () => void;
    carId: string;
    userId: string;
    selectedYear: number;
    logEntries: LogEntry[];
    t: any;
    language: string;
    /** If provided, use this as the start date instead of Jan 1 (for purchase year) */
    purchaseDate?: string;
    /** Earliest year the user is allowed to enter mileage for */
    minAllowedYear?: number;
}

export function MissingMileageModal({
    isOpen,
    onClose,
    onSaved,
    carId,
    userId,
    selectedYear,
    logEntries,
    t,
    language,
    purchaseDate,
    minAllowedYear
}: MissingMileageModalProps) {
    const isCurrentYear = selectedYear === new Date().getFullYear();
    const [startMileage, setStartMileage] = useState('');
    const [endMileage, setEndMileage] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    // Reset inputs when opening or changing year
    useEffect(() => {
        if (isOpen) {
            setStartMileage('');
            setEndMileage('');
        }
    }, [isOpen, selectedYear]);

    // Calc dates – for purchase year, use the actual purchase/first-entry date instead of Jan 1
    const isPurchaseYear = !!purchaseDate;
    const startDateStr = isPurchaseYear ? purchaseDate! : `${selectedYear}-01-01`;
    const endDateStr = isCurrentYear ? new Date().toISOString().split('T')[0] : `${selectedYear}-12-31`;

    // Find nearby entries (within 15 days)
    const findNearby = (targetDateStr: string) => {
        const target = new Date(targetDateStr).getTime();
        const diffLimit = 15 * 24 * 60 * 60 * 1000;
        
        const matches = logEntries
            .filter(e => e.mileage && e.mileage > 0)
            .map(e => ({
                ...e,
                diff: Math.abs(new Date(e.date).getTime() - target)
            }))
            .filter(e => e.diff <= diffLimit)
            .sort((a, b) => a.diff - b.diff);
            
        return matches.length > 0 ? matches[0] : null;
    };

    const nearbyStart = useMemo(() => findNearby(startDateStr), [logEntries, startDateStr]);
    const nearbyEnd = useMemo(() => findNearby(endDateStr), [logEntries, endDateStr]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const recordsToInsert = [];
            
            if (startMileage && !isNaN(parseInt(startMileage))) {
                recordsToInsert.push({
                    id: generateUUID(),
                    car_id: carId,
                    user_id: userId,
                    date: startDateStr,
                    mileage: parseInt(startMileage),
                    title: null
                });
            }
            
            if (endMileage && !isNaN(parseInt(endMileage))) {
                recordsToInsert.push({
                    id: generateUUID(),
                    car_id: carId,
                    user_id: userId,
                    date: endDateStr,
                    mileage: parseInt(endMileage),
                    title: null
                });
            }

            if (recordsToInsert.length > 0) {
                // Insert log entries
                const { error: logErr } = await supabase.from('log_entries').insert(recordsToInsert);
                if (logErr) throw logErr;

                // Also need to insert dummy info expenses for these so they show up / satisfy logic if needed
                // But mainly because some parts of the app expect at least one expense per log_entry (optional but good)
                const expenses = recordsToInsert.map(r => ({
                    id: generateUUID(),
                    car_id: carId,
                    user_id: userId,
                    log_entry_id: r.id,
                    type: 'info',
                    title: '',
                    amount: 0,
                    date: r.date
                }));
                const { error: expErr } = await supabase.from('expenses').insert(expenses);
                if (expErr) throw expErr;

                onSaved();
                onClose();
            }
        } catch (err) {
            console.error('Error saving mileages:', err);
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    const formatDateShort = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString(language, { day: '2-digit', month: 'short', year: 'numeric' });
    };

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
            
            <div className="relative bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl w-full max-w-md p-8 animate-in fade-in zoom-in-95 duration-200">
                <button 
                    onClick={onClose}
                    className="absolute top-6 right-6 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                >
                    <X size={24} />
                </button>

                <div className="mb-8">
                    <h3 className="text-2xl font-black text-gray-900 dark:text-white mb-2">
                        {t.statistics.mileageModal.title}
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 font-medium leading-relaxed">
                        {t.statistics.mileageModal.description.replace('{year}', selectedYear.toString())}
                    </p>
                </div>

                <div className="space-y-6">
                    {/* Start Mileage */}
                    <div>
                        <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">
                            {isPurchaseYear
                                ? (t.statistics.mileageModal.purchaseDate || 'Pirkšanas datums')
                                : t.statistics.mileageModal.startOfYear}
                        </label>
                        {nearbyStart ? (
                            <div className="bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-100 dark:border-emerald-500/20 rounded-2xl p-4 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                                        <Check size={18} />
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-gray-900 dark:text-white">
                                            {nearbyStart.mileage?.toLocaleString()} km
                                        </p>
                                        <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                            {formatDateShort(nearbyStart.date)}
                                        </p>
                                    </div>
                                </div>
                                <span className="text-[10px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest">
                                    Atrasts
                                </span>
                            </div>
                        ) : (
                            <input
                                type="number"
                                value={startMileage}
                                onChange={e => setStartMileage(e.target.value)}
                                placeholder={t.statistics.mileageModal.placeholder}
                                className="w-full bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 rounded-2xl px-5 py-4 focus:ring-2 focus:ring-blue-500 outline-none font-bold text-gray-900 dark:text-white transition-all"
                            />
                        )}
                    </div>

                    {/* End Mileage */}
                    <div>
                        <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">
                            {isCurrentYear ? t.statistics.mileageModal.currentMileage : t.statistics.mileageModal.endOfYear}
                        </label>
                        {nearbyEnd ? (
                            <div className="bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-100 dark:border-emerald-500/20 rounded-2xl p-4 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                                        <Check size={18} />
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-gray-900 dark:text-white">
                                            {nearbyEnd.mileage?.toLocaleString()} km
                                        </p>
                                        <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                            {formatDateShort(nearbyEnd.date)}
                                        </p>
                                    </div>
                                </div>
                                <span className="text-[10px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest">
                                    Atrasts
                                </span>
                            </div>
                        ) : (
                            <input
                                type="number"
                                value={endMileage}
                                onChange={e => setEndMileage(e.target.value)}
                                placeholder={t.statistics.mileageModal.placeholder}
                                className="w-full bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 rounded-2xl px-5 py-4 focus:ring-2 focus:ring-blue-500 outline-none font-bold text-gray-900 dark:text-white transition-all"
                            />
                        )}
                    </div>
                </div>

                <div className="mt-8 flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 py-4 px-4 rounded-2xl font-bold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                    >
                        {t.common.cancel}
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isSaving || (!startMileage && !endMileage)}
                        className="flex-1 py-4 px-4 rounded-2xl font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95"
                    >
                        {isSaving ? (
                            <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto" />
                        ) : t.statistics.mileageModal.save}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
